import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { invoicesApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { emitAppEvent } from "@/utils/appEvents";
import { formatDateOnly } from "@/utils/progress";
import { formatCurrency, formatDateTime } from "@/utils/currency";
import {
  invoiceStatusLabels, invoiceStatusBadgeClasses, paymentStatusLabels,
} from "@/components/rechnungen/invoiceStatus";
import { InvoiceForm, type InvoiceFormPayload } from "@/components/rechnungen/InvoiceForm";
import { InvoicePdfPreview } from "@/components/rechnungen/InvoicePdfPreview";
import { InvoiceStatusTimeline } from "@/components/rechnungen/InvoiceStatusTimeline";
import { InvoiceComments } from "@/components/rechnungen/InvoiceComments";
import { InvoiceReviewActions } from "@/components/rechnungen/InvoiceReviewActions";
import type {
  InvoiceDetail, InvoiceStatusLogEntry, InvoiceComment, InvoiceTransitionAction,
} from "@/types";
import { ArrowLeft, Loader2, AlertCircle, Pencil, Trash2, FileText } from "lucide-react";

/**
 * Rechnungs-Detail: besitzt alle Daten/Handler (Muster
 * BauvorhabenDetailPage, aber ohne Outlet — Bearbeiten läuft inline,
 * die Review-UI ist ohnehin das Kernstück der Detailspalte).
 * id === 'new' = Anlegeformular.
 */
export function RechnungenDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isApprover } = useAuth();
  const isNew = id === 'new';

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [statusLog, setStatusLog] = useState<InvoiceStatusLogEntry[]>([]);
  const [comments, setComments] = useState<InvoiceComment[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Load-Key: Mount/ID-Wechsel UND manuelle Reloads (nach Aktionen) feuern.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setIsLoading(true);
    setError('');
    invoicesApi
      .getById(Number(id))
      .then((response) => {
        if (cancelled) return;
        setInvoice(response.data.data.invoice);
        setStatusLog(response.data.data.status_log);
        setComments(response.data.data.comments);
      })
      .catch(() => {
        if (!cancelled) setError('Rechnung nicht gefunden');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, reloadKey]);

  const handleSave = (payload: InvoiceFormPayload, file: File | null) => {
    setSaveError('');
    setIsSaving(true);
    const apiPayload = {
      direction: payload.direction,
      invoice_number: payload.invoice_number,
      contact_id: payload.contact_id,
      bauvorhaben_id: payload.bauvorhaben_id,
      gewerk_id: payload.gewerk_id,
      amount_net: payload.amount_net === '' ? null : Number(payload.amount_net),
      amount_tax: payload.amount_tax === '' ? null : Number(payload.amount_tax),
      amount_gross: Number(payload.amount_gross),
      tax_rate_percent: payload.tax_rate_percent === '' ? null : Number(payload.tax_rate_percent),
      invoice_date: payload.invoice_date,
      due_date: payload.due_date || null,
      description: payload.description.trim() || null,
      notes: payload.notes.trim() || null,
    };

    const request = isNew
      ? invoicesApi.create(apiPayload)
      : invoicesApi.update(Number(id), apiPayload);

    request
      .then((response) => {
        const savedId = isNew ? (response.data.data as { id: number }).id : Number(id);
        const upload = file
          ? invoicesApi.uploadFile(savedId, file).catch((err) => Promise.reject({
              upload: true,
              message: (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Hochladen der PDF',
            }))
          : Promise.resolve();
        return upload.then(() => savedId);
      })
      .then((savedId) => {
        // Gemountete Listen-Spalte still nachladen.
        emitAppEvent('rechnungen:changed');
        if (isNew) {
          navigate(`/rechnungen/${savedId}`, { replace: true });
        } else {
          setIsEditing(false);
          reload();
        }
      })
      .catch((err) => {
        setSaveError(err?.response?.data?.error || err?.message || 'Fehler beim Speichern');
      })
      .finally(() => setIsSaving(false));
  };

  const handleTransition = async (action: InvoiceTransitionAction, comment?: string) => {
    if (!invoice) return;
    await invoicesApi.transition(invoice.id, action, comment);
    // Listen-Spalte + Detail (Status, Timeline, Stammdaten) mitziehen.
    emitAppEvent('rechnungen:changed');
    reload();
  };

  const handleAddComment = async (comment: string, isInternal: boolean) => {
    if (!invoice) return;
    await invoicesApi.addComment(invoice.id, comment, isInternal);
    reload();
  };

  const handleUploadFile = (file: File) => {
    if (!invoice) return;
    invoicesApi
      .uploadFile(invoice.id, file)
      .then(() => {
        emitAppEvent('rechnungen:changed');
        reload();
      })
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Hochladen'));
  };

  const handleDelete = () => {
    if (!invoice) return;
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) return;
    invoicesApi
      .remove(invoice.id)
      .then(() => {
        emitAppEvent('rechnungen:changed');
        navigate('/rechnungen');
      })
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Löschen'));
  };

  if (isNew) {
    return (
      <div className="p-4">
        <Link to="/rechnungen" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Rechnungen
        </Link>
        <div className="card p-6">
          <InvoiceForm
            isNew
            isSaving={isSaving}
            error={saveError}
            onSave={handleSave}
            onCancel={() => navigate('/rechnungen')}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="p-4">
        <div className="card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Link to="/rechnungen" className="btn btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  const editableStatuses = ['erfasst', 'korrektur_erforderlich'];
  const canEdit = isAdmin && invoice.status === 'erfasst';
  const canReplaceFile = isAdmin && editableStatuses.includes(invoice.status);

  return (
    <div className="p-4">
      <Link to="/rechnungen" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" />
        Rechnungen
      </Link>

      {/* Kopf: Nummer + Status + Aktionen */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{invoice.invoice_number}</h1>
            <p className="text-sm text-gray-500">
              {invoice.direction === 'eingang' ? 'Eingangsrechnung' : 'Ausgangsrechnung'}
              {invoice.description ? ` · ${invoice.description}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${invoiceStatusBadgeClasses[invoice.status as keyof typeof invoiceStatusBadgeClasses]}`}>
              {invoiceStatusLabels[invoice.status as keyof typeof invoiceStatusLabels]}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                invoice.payment_status === 'bezahlt' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {paymentStatusLabels[invoice.payment_status]}
            </span>
          </div>
        </div>
        <InvoiceReviewActions
          invoice={invoice}
          isAdmin={isAdmin}
          isApprover={isApprover}
          isBusy={isSaving}
          onTransition={handleTransition}
        />
      </div>

      {isEditing ? (
        <div className="card p-6 mb-6">
          <InvoiceForm
            isNew={false}
            initial={invoice}
            isSaving={isSaving}
            error={saveError}
            onSave={handleSave}
            onCancel={() => {
              setIsEditing(false);
              setSaveError('');
            }}
          />
        </div>
      ) : (
        <>
          {/* Stammdaten */}
          <div className="card p-6 mb-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Stammdaten</h2>
              {isAdmin && (
                <div className="flex gap-2">
                  {canEdit && (
                    <button onClick={() => setIsEditing(true)} className="btn btn-secondary inline-flex items-center gap-2">
                      <Pencil className="h-4 w-4" />
                      Bearbeiten
                    </button>
                  )}
                  {editableStatuses.includes(invoice.status) && (
                    <button onClick={handleDelete} className="btn btn-danger inline-flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      Löschen
                    </button>
                  )}
                </div>
              )}
            </div>
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Rechnungsnummer</dt>
                <dd className="text-gray-900">{invoice.invoice_number}</dd>
              </div>
              <div>
                <dt className="text-gray-500">{invoice.direction === 'eingang' ? 'Lieferant' : 'Empfänger'}</dt>
                <dd className="text-gray-900">
                  {invoice.contact_id && invoice.contact_name ? (
                    <Link to={`/contacts/${invoice.contact_id}`} className="text-primary-600 hover:text-primary-700">
                      {invoice.contact_name}
                    </Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Bauvorhaben</dt>
                <dd className="text-gray-900">
                  {invoice.bauvorhaben_id && invoice.bauvorhaben_name ? (
                    <Link to={`/bauvorhaben/${invoice.bauvorhaben_id}`} className="text-primary-600 hover:text-primary-700">
                      {invoice.bauvorhaben_name}
                    </Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Gewerk</dt>
                <dd className="text-gray-900">{invoice.gewerk_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Betrag (brutto)</dt>
                <dd className="font-semibold text-gray-900">{formatCurrency(invoice.amount_gross)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Netto / MwSt</dt>
                <dd className="text-gray-900">
                  {invoice.amount_net != null ? formatCurrency(invoice.amount_net) : '—'}
                  {' / '}
                  {invoice.amount_tax != null ? formatCurrency(invoice.amount_tax) : '—'}
                  {invoice.tax_rate_percent != null ? ` (${invoice.tax_rate_percent} %)` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Rechnungsdatum</dt>
                <dd className="text-gray-900">{formatDateOnly(invoice.invoice_date) || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Fällig am</dt>
                <dd className="text-gray-900">{formatDateOnly(invoice.due_date) || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Erfasst von</dt>
                <dd className="text-gray-900">
                  {invoice.created_by_name || '—'} · {formatDateTime(invoice.created_at)}
                </dd>
              </div>
              {invoice.lexware_exported_at && (
                <div>
                  <dt className="text-gray-500">Lexware-Übergabe</dt>
                  <dd className="text-gray-900">
                    {formatDateTime(invoice.lexware_exported_at)}
                    {invoice.lexware_exported_by_name ? ` von ${invoice.lexware_exported_by_name}` : ''}
                  </dd>
                </div>
              )}
              {invoice.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notizen</dt>
                  <dd className="whitespace-pre-wrap text-gray-900">{invoice.notes}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* PDF */}
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-400" />
              Dokument
            </h2>
            {invoice.has_file ? (
              <InvoicePdfPreview invoiceId={invoice.id} fileName={invoice.file_original_name} />
            ) : (
              <p className="text-sm text-gray-500">Keine Datei hochgeladen.</p>
            )}
            {canReplaceFile && (
              <div className="mt-3">
                <label className="label">{invoice.has_file ? 'PDF ersetzen' : 'PDF hochladen'}</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadFile(file);
                    e.target.value = '';
                  }}
                  className="input"
                />
                <p className="mt-1 text-xs text-gray-500">Nur PDF, maximal 10 MB.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Status-Timeline */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statusverlauf</h2>
        <InvoiceStatusTimeline entries={statusLog} />
      </div>

      {/* Kommentare */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Kommentare</h2>
        <InvoiceComments comments={comments} isAdmin={isAdmin} onAdd={handleAddComment} />
      </div>
    </div>
  );
}